import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import OrderHeader from '../components/order/OrderHeader';
import Timeline from '../components/order/Timeline';
import { getPaymentStatus } from '../api';
import './Order.css';

// Where Mollie sends the curator back to. This check is also what makes the
// payment count in local development, since Mollie can't call a webhook on
// localhost - and it's the payment landing that takes the special tiles out
// of stock, backend side.
const CHECK_EVERY_MS = 4000;
const MAX_CHECKS = 15;

function ThankYou() {
  const { t, i18n } = useTranslation();
  const { publicId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('checking');
  const [shippingOption, setShippingOption] = useState(null);
  // How many delivery days this order was sold with - a setting in the
  // admin panel, kept on the order.
  const [deliveryDays, setDeliveryDays] = useState(2);
  // A custom order is tried again from its payment link, not a new order.
  const [isCustom, setIsCustom] = useState(false);
  // A custom order agreed to a day; then that day is what we say.
  const [readyBy, setReadyBy] = useState(null);
  const [error, setError] = useState(null);

  // Mollie can still say "open" for a moment after sending someone back,
  // and there is no webhook on localhost - so while it's pending this asks
  // again every few seconds for a minute, rather than waiting forever on
  // the first answer.
  const [checks, setChecks] = useState(0);
  useEffect(() => {
    if (status !== 'pending' || checks >= MAX_CHECKS) return undefined;
    const timer = setTimeout(() => setChecks((n) => n + 1), CHECK_EVERY_MS);
    return () => clearTimeout(timer);
  }, [status, checks]);

  useEffect(() => {
    getPaymentStatus(publicId, token)
      .then((data) => {
        setStatus(data.payment_status);
        setShippingOption(data.shipping_option);
        if (data.delivery_days) setDeliveryDays(data.delivery_days);
        setIsCustom(Boolean(data.is_custom));
        setReadyBy(data.ready_by || null);
        // The order page keeps a draft in sessionStorage so a round trip to
        // Mollie doesn't wipe the form - once actually paid, that draft has
        // done its job and shouldn't resurface if this browser starts a new
        // order later.
        if (data.payment_status === 'paid') {
          sessionStorage.removeItem('mosaik_order_draft');
        }
      })
      .catch((err) => setError(err.message));
  }, [publicId, token, checks]);

  // Only "ship_split" has anyone else to invite - the other two are a
  // regular order with no share link and no deadline.
  const isGroupOrder = shippingOption === 'ship_split';

  const readyDay =
    readyBy &&
    new Date(`${readyBy}T12:00:00`).toLocaleDateString(i18n.resolvedLanguage === 'en' ? 'en-GB' : 'nl-NL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });

  let mark = 'waiting';
  let title;
  let body = null;
  let next = null;

  if (error) {
    mark = 'failed';
    title = t('thankYou.couldNotCheck');
    body = <p className="of-error">{error}</p>;
  } else if (status === 'checking') {
    title = t('thankYou.checking');
  } else if (status === 'failed' || status === 'canceled' || status === 'expired') {
    mark = 'failed';
    title = t(`thankYou.reason.${status}`);
    body = (
      <>
        <p className="ty-lede">{t('thankYou.notCharged')}</p>
        {/* The draft is still in sessionStorage, so the form comes back filled in. */}
        <Link
          to={isCustom ? `/order/${publicId}/pay?token=${encodeURIComponent(token || '')}` : '/order'}
          className="of-btn ty-restart"
        >
          {t('thankYou.restart')}
          <span aria-hidden="true">&rarr;</span>
        </Link>
      </>
    );
  } else if (status === 'authorized' || status === 'pending') {
    title = t(`thankYou.${status}.title`);
    body = (
      <>
        <p className="ty-lede">{t(`thankYou.${status}.body`)}</p>
        {/* Still open after a minute: most likely it was never finished at
            the bank. A custom order can go straight back to paying. */}
        {status === 'pending' && checks >= MAX_CHECKS && isCustom && (
          <Link to={`/order/${publicId}/pay?token=${encodeURIComponent(token || '')}`} className="of-btn ty-restart">
            {t('thankYou.payAgain')}
            <span aria-hidden="true">&rarr;</span>
          </Link>
        )}
      </>
    );
  } else {
    mark = 'done';
    title = t('thankYou.paid.title');
    body = <p className="ty-lede">{t('thankYou.paid.body')}</p>;
    next = isGroupOrder
      ? [
          { when: t('thankYou.now'), what: t('thankYou.group.email') },
          { when: t('thankYou.next'), what: t('thankYou.group.share') },
          { when: t('thankYou.group.untilDeadline'), what: t('thankYou.group.follow') },
          readyBy
            ? { when: readyDay, what: t('thankYou.group.boardOn') }
            : { when: t('thankYou.group.afterDeadline'), what: t('thankYou.group.board', { count: deliveryDays }) },
        ]
      : [
          { when: t('thankYou.now'), what: t('thankYou.regular.email') },
          {
            when: readyBy ? readyDay : t('thankYou.regular.within', { count: deliveryDays }),
            what: shippingOption === 'pickup' ? t('thankYou.regular.pickup') : t('thankYou.regular.ship'),
          },
          { when: t('thankYou.next'), what: t('thankYou.regular.handOut') },
        ];
  }

  return (
    <div className="atelier of">
      <OrderHeader home />

      <main className="ty">
        <div className={`ty-mark ty-mark--${mark}`} aria-hidden="true">
          {mark === 'done' && (
            <svg viewBox="0 0 52 52">
              <path d="M14 27 l8 8 l17 -18" />
            </svg>
          )}
          {mark === 'failed' && <span>!</span>}
          {mark === 'waiting' && <span className="ty-dots" />}
        </div>

        <h1 className="ty-title">{title}</h1>
        {body}

        {next && (
          <section className="ty-next">
            <h2>{t('thankYou.whatNow')}</h2>
            <Timeline items={next} />
          </section>
        )}

        {mark === 'done' && <p className="ty-close">{t('thankYou.closeNote')}</p>}
      </main>
    </div>
  );
}

export default ThankYou;
